// CerrarEvento — captain/supervisor view of closure progress + the close
// button (Sophi App v2 Piece 6). One screen handles both pre- and
// post-initiation states. Polls /events/:id/closure-status every 8s to
// keep the team-progress view fresh.
//
// Admin force-close lands in Piece 11 with a reason input. This slice is
// captain-only happy path: wait for all reports, then finalize.

import { useEffect, useState } from 'preact/hooks';
import {
  getClosureStatus, initiateClosure, finalizeClosure, submitReportFor,
  setTipDistribution, setOtrosPropinas,
  ApiError,
  type ClosureStatus,
} from '../lib/api';
import { haptic } from '../lib/telegram';
import { DepositoPanel } from '../components/DepositoPanel';

const POLL_INTERVAL_MS = 8000;

interface CerrarEventoProps {
  eventId: string;
  eventTitle?: string;
  onClose: () => void;
  /** Optional: open the team results screen after a successful finalize. */
  onShowResults?: () => void;
}

export function CerrarEvento({ eventId, eventTitle, onClose, onShowResults }: CerrarEventoProps) {
  const [status, setStatus] = useState<ClosureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForceForm, setShowForceForm] = useState(false);
  const [forceReason, setForceReason] = useState('');
  // Set once finalize succeeds so we can offer "Ver resultados" without depending
  // on closure-status (which returns not_initiated once the bot deletes state).
  const [justFinalized, setJustFinalized] = useState(false);
  // Inline tip-config (captain-less path): set distribution / Otros before initiating.
  const [savingConfig, setSavingConfig] = useState(false);
  // Proxy report state — captain submits on behalf of a pending team member.
  const [proxyOpen, setProxyOpen] = useState<string | null>(null); // staffId
  const [proxyVentas, setProxyVentas] = useState('');
  const [proxyPropinas, setProxyPropinas] = useState('');
  const [proxyError, setProxyError] = useState<string | null>(null);

  async function refresh() {
    try {
      const s = await getClosureStatus(eventId);
      setStatus(s);
      setLoading(false);
    } catch (e) {
      setError((e as Error).message);
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [eventId]);

  // Poll every 8s while visible.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function tick() {
      if (document.hidden) { timer = setTimeout(tick, POLL_INTERVAL_MS); return; }
      if (!cancelled) await refresh();
      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    }
    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [eventId]);

  async function onInitiate() {
    if (busy) return;
    setBusy(true);
    setError(null);
    haptic('warning');
    try {
      await initiateClosure(eventId);
      await refresh();
    } catch (e) {
      setError(translateError((e as Error).message));
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  async function onSetDistribution(distribucion: 'Solo_Camareros' | 'Todos_Los_Roles' | 'Pool_Total') {
    if (savingConfig) return;
    setSavingConfig(true);
    setError(null);
    haptic('light');
    try {
      await setTipDistribution(eventId, distribucion);
      await refresh();
    } catch (e) {
      setError(translateError((e as Error).message));
      haptic('error');
    } finally {
      setSavingConfig(false);
    }
  }

  async function onSetOtros(valor: 'Si' | 'No') {
    if (savingConfig) return;
    setSavingConfig(true);
    setError(null);
    haptic('light');
    try {
      await setOtrosPropinas(eventId, valor);
      await refresh();
    } catch (e) {
      setError(translateError((e as Error).message));
      haptic('error');
    } finally {
      setSavingConfig(false);
    }
  }

  async function onFinalize() {
    if (busy || !status) return;
    if (!confirm('Cerrar el evento ahora. Los pagos se calculan automáticamente. ¿Confirmas?')) return;
    setBusy(true);
    setError(null);
    haptic('warning');
    try {
      await finalizeClosure(eventId);
      // Server is async (202); poll a couple times to catch the finalizing → completed transition.
      await new Promise((r) => setTimeout(r, 1500));
      await refresh();
      setJustFinalized(true);
    } catch (e) {
      setError((e as Error).message);
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  function openProxyForm(staffId: string) {
    setProxyOpen(staffId);
    setProxyVentas('');
    setProxyPropinas('');
    setProxyError(null);
  }
  function closeProxyForm() {
    setProxyOpen(null);
    setProxyVentas('');
    setProxyPropinas('');
    setProxyError(null);
  }

  async function submitProxyReport(targetStaffId: string, force = false) {
    const v = Number(proxyVentas);
    const p = Number(proxyPropinas);
    if (!Number.isFinite(v) || v < 0) { setProxyError('Ventas inválidas'); return; }
    if (!Number.isFinite(p) || p < 0) { setProxyError('Propinas inválidas'); return; }
    setProxyError(null);
    setBusy(true);
    haptic('light');
    try {
      await submitReportFor(eventId, { targetStaffId, ventas: v, propinas: p, force });
      haptic('success');
      closeProxyForm();
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const existing = e.body && (e.body as any).existing;
        if (existing && !existing.edited) {
          // Confirm replacement and retry with force=true.
          if (confirm(`Ya hay un reporte para este staff (ventas RD$ ${existing.ventas}, propinas RD$ ${existing.propinas}). ¿Reemplazar?`)) {
            await submitProxyReport(targetStaffId, true);
            return;
          }
          setProxyError('Cancelado.');
          haptic('warning');
        } else {
          setProxyError(existing && existing.edited
            ? 'Este staff ya editó su reporte una vez. Pide a Operaciones que ayude.'
            : (e.message || 'Conflicto en el reporte'));
          haptic('error');
        }
      } else {
        setProxyError(translateError((e as Error).message));
        haptic('error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onForceFinalize() {
    if (busy || !status) return;
    const reason = forceReason.trim();
    if (!reason) { setError('Necesitas escribir una razón para forzar el cierre.'); return; }
    if (!confirm(`Forzar cierre con reportes incompletos.\n\nQuedan ${status.remaining.length} sin reportar.\nRazón: "${reason}"\n\n¿Confirmas?`)) return;
    setBusy(true);
    setError(null);
    haptic('warning');
    try {
      await finalizeClosure(eventId, { force: true, reason });
      await new Promise((r) => setTimeout(r, 1500));
      await refresh();
      setShowForceForm(false);
      setForceReason('');
      setJustFinalized(true);
    } catch (e) {
      setError((e as Error).message);
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body"><div class="loading"><span class="spinner" /></div></div>
      </div>
    );
  }

  // After a successful finalize the bot deletes its closure state, so polling
  // would bounce back to the "iniciar" screen. Show a terminal "cerrado" card
  // with a CTA into the team results instead.
  if (justFinalized) {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body">
          <div class="mireporte-blocked">
            <div class="mireporte-blocked-icon">✅</div>
            <h3>Evento cerrado</h3>
            <p class="mireporte-meta">Los pagos se calcularon. Cada quien recibe su desglose completo por DM de Sophi.</p>
          </div>
          {/* Captain uploads the client-deposit voucher right here (skips for monthly / no-factura). */}
          <DepositoPanel eventId={eventId} />
          {onShowResults && (
            <button class="btn primary" onClick={onShowResults}>🏆 Ver resultados del equipo</button>
          )}
          <button class="btn ghost" onClick={onClose}>Volver</button>
        </div>
      </div>
    );
  }
  if (!status) {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body">
          <div class="mireporte-error">⚠️ {error || 'No pudimos cargar el cierre.'}</div>
          <button class="btn primary" onClick={onClose}>Volver</button>
        </div>
      </div>
    );
  }

  // Only block users the server says are NOT closers. Gating on isCloser (vs.
  // canInitiate/canFinalize) keeps an admin/supervisor on the collection view
  // right after they initiate — those flags go false mid-collection for a
  // non-captain closer, which used to show "solo el capitán" even though the
  // event had already moved to Recolectando. `=== false` is fail-open across a
  // version skew (old backend omits isCloser); the server still gates actions.
  if (status.isCloser === false) {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body">
          <div class="mireporte-blocked">
            <div class="mireporte-blocked-icon">🚫</div>
            <h3>Solo el capitán/supervisor cierra el evento</h3>
            <p class="mireporte-meta">Si necesitas iniciar el cierre, pídele al capitán.</p>
          </div>
          <button class="btn primary" onClick={onClose}>Volver</button>
        </div>
      </div>
    );
  }

  const reportedCount = status.team.filter((t) => t.reported).length;
  const total = status.team.length;
  const finalized = status.state === 'finalizing';
  // Closure can only start once the deliberate-choice tip config is set:
  // distribution chosen AND no Otro row left without an in-pool flag.
  const configReady = !!status.tipDistribution && (status.otrosPendientes?.length ?? 0) === 0;
  // Fase 2 gate (Codex P2, PR #286): rate-less events cannot close, and unlike
  // the tip config nobody here can fix it — Ventas fills the rates in the CRM.
  // Surface it up front as a blocker instead of letting the 409 explain it.
  const tarifasPendientes = status.tarifasPendientes === true;

  return (
    <div class="event-chat-screen">
      <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
      <div class="mireporte-body">
        {status.state === 'not_initiated' && (
          <>
            <h3>Iniciar cierre del evento</h3>
            <p class="mireporte-meta">
              Cuando inicies el cierre, cada miembro podrá registrar sus ventas y propinas desde la app. También sigue funcionando el reporte por el grupo de Telegram.
            </p>

            {/* Inline tip config — the captain-less path: nobody picked this in
                the group. Setting it here is what lets Ops initiate any event. */}
            <div class="closure-config">
              <div class="closure-config-block">
                <div class="closure-config-label">Distribución de propinas</div>
                <div class="closure-config-options">
                  <button
                    class={`closure-config-opt ${status.tipDistribution === 'Solo_Camareros' ? 'selected' : ''}`}
                    disabled={savingConfig}
                    onClick={() => onSetDistribution('Solo_Camareros')}
                  >
                    Solo camareros
                  </button>
                  <button
                    class={`closure-config-opt ${status.tipDistribution === 'Todos_Los_Roles' ? 'selected' : ''}`}
                    disabled={savingConfig}
                    onClick={() => onSetDistribution('Todos_Los_Roles')}
                  >
                    Todos los roles
                  </button>
                  <button
                    class={`closure-config-opt ${status.tipDistribution === 'Pool_Total' ? 'selected' : ''}`}
                    disabled={savingConfig}
                    onClick={() => onSetDistribution('Pool_Total')}
                  >
                    Pool total (partes iguales)
                  </button>
                </div>
                {!status.tipDistribution && (
                  <p class="closure-config-hint">Elige cómo se reparten las propinas antes de iniciar.</p>
                )}
              </div>

              {status.otrosPendientes && status.otrosPendientes.length > 0 && (
                <div class="closure-config-block">
                  <div class="closure-config-label">¿El personal "Otro" entra en propinas?</div>
                  <p class="closure-config-hint">
                    Pendiente: {status.otrosPendientes.map((o) => o.nombre).join(', ')}
                  </p>
                  <div class="closure-config-options">
                    <button class="closure-config-opt" disabled={savingConfig} onClick={() => onSetOtros('Si')}>
                      Sí, entran
                    </button>
                    <button class="closure-config-opt" disabled={savingConfig} onClick={() => onSetOtros('No')}>
                      No, se quedan las suyas
                    </button>
                  </div>
                </div>
              )}
            </div>

            {tarifasPendientes && (
              <div class="mireporte-error">
                ⚠️ Este evento todavía no tiene tarifas. Ventas debe completarlas en el CRM antes de
                poder cerrarlo.
              </div>
            )}
            {error && <div class="mireporte-error">⚠️ {error}</div>}
            <button
              class="btn primary"
              disabled={busy || savingConfig || !configReady || tarifasPendientes}
              onClick={onInitiate}
            >
              {busy ? 'Iniciando...' : 'Iniciar cierre'}
            </button>
            {!configReady && !tarifasPendientes && (
              <p class="mireporte-meta closure-config-blocked">
                Completa la configuración de propinas para iniciar el cierre.
              </p>
            )}
          </>
        )}

        {status.state !== 'not_initiated' && (
          <>
            <h3>Reportes del equipo</h3>
            <div class="closure-progress-bar">
              <div class="closure-progress-fill" style={{ width: `${(reportedCount / total) * 100}%` }} />
            </div>
            <p class="mireporte-meta closure-progress-count">
              {reportedCount} de {total} {reportedCount === 1 ? 'reporte' : 'reportes'} recibidos
            </p>

            <ul class="closure-team-list">
              {status.team.map((m) => {
                // Proxy-reporting is captain-or-Ops only (mirrors the server's
                // isCaptainOrAdmin gate on /report-for). Supervisors who can
                // otherwise drive closure are intentionally excluded.
                const canProxy = !m.reported && (status.isCaptain || !!status.isAdmin);
                const isOpen = proxyOpen === m.staffId;
                return (
                  <li key={m.staffId} class={`closure-team-row ${m.reported ? 'reported' : 'pending'}`}>
                    <div class="closure-team-row-head">
                      <div class="closure-team-name">{m.nombre}</div>
                      {canProxy && !isOpen && (
                        <button class="closure-proxy-btn" onClick={() => openProxyForm(m.staffId)}>
                          Reportar por {m.nombre.split(' ')[0]}
                        </button>
                      )}
                    </div>
                    <div class="closure-team-meta">
                      {m.reported
                        ? <>
                            <span class="closure-source-badge">{m.source === 'app' ? '📱' : '💬'}</span>
                            ventas RD$ {fmt(m.ventas || 0)} · propinas RD$ {fmt(m.propinas || 0)}
                          </>
                        : <span class="closure-pending-label">esperando reporte</span>}
                    </div>
                    {isOpen && (
                      <div class="closure-proxy-form">
                        <div class="closure-proxy-fields">
                          <input
                            type="number" min="0" step="1" inputMode="numeric"
                            placeholder="Ventas DOP"
                            value={proxyVentas}
                            onInput={(e) => setProxyVentas((e.target as HTMLInputElement).value)}
                          />
                          <input
                            type="number" min="0" step="1" inputMode="numeric"
                            placeholder="Propinas DOP"
                            value={proxyPropinas}
                            onInput={(e) => setProxyPropinas((e.target as HTMLInputElement).value)}
                          />
                        </div>
                        {proxyError && <div class="mireporte-error">⚠️ {proxyError}</div>}
                        <div class="closure-proxy-actions">
                          <button class="btn primary" disabled={busy} onClick={() => submitProxyReport(m.staffId)}>
                            {busy ? 'Enviando…' : 'Enviar'}
                          </button>
                          <button class="btn ghost" onClick={closeProxyForm}>Cancelar</button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {error && <div class="mireporte-error">⚠️ {error}</div>}

            {finalized
              ? <div class="closure-finalizing">⏳ Calculando resultados…</div>
              : status.canFinalize
              ? <button class="btn primary" disabled={busy} onClick={onFinalize}>
                  {busy ? 'Cerrando…' : 'Cerrar evento y calcular pagos'}
                </button>
              : <p class="mireporte-meta">El botón de cierre se activa cuando todos hayan reportado.</p>}

            {/* Admin force-close (Piece 11) — bypasses all-reports-in gate. */}
            {!finalized && status.canForceFinalize && !showForceForm && (
              <button
                class="btn ghost ops-force-btn"
                onClick={() => setShowForceForm(true)}
                style={{ marginTop: 12 }}
              >
                🛡️ Forzar cierre (Ops)
              </button>
            )}
            {!finalized && status.canForceFinalize && showForceForm && (
              <div class="ops-force-form">
                <h4>Forzar cierre con reportes incompletos</h4>
                <p class="mireporte-meta">
                  Esta acción cierra el evento ignorando que falten reportes. Solo Ops puede hacerlo. La razón se guarda en auditoría.
                </p>
                <label class="mireporte-field">
                  <span>Razón (requerida)</span>
                  <input
                    type="text"
                    value={forceReason}
                    onInput={(e) => setForceReason((e.target as HTMLInputElement).value)}
                    placeholder="ej. Capitán no respondió en 6h"
                  />
                </label>
                <div class="mireporte-actions">
                  <button class="btn primary" disabled={busy || !forceReason.trim()} onClick={onForceFinalize}>
                    {busy ? 'Forzando…' : 'Forzar cierre'}
                  </button>
                  <button class="btn ghost" onClick={() => { setShowForceForm(false); setForceReason(''); }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Header({ eventId, eventTitle, onClose }: { eventId: string; eventTitle?: string; onClose: () => void }) {
  return (
    <header class="event-chat-header">
      <button class="event-chat-back" onClick={onClose} aria-label="Cerrar">←</button>
      <div class="event-chat-titles">
        <div class="event-chat-title">Cerrar evento · {eventTitle || eventId}</div>
        <div class="event-chat-sub">Progreso de reportes</div>
      </div>
    </header>
  );
}

function fmt(n: number): string {
  return n.toLocaleString('es-DO');
}

// Turn server error codes into actionable DR Spanish. The closure-config gates
// (no_tip_distribution / otros_sin_flag) used to surface as `bridge_unavailable`
// — a dead-end "service down" message — leaving Ops to retry blindly. Model:
// SwapPicker.translateError. Unknown codes pass through unchanged.
function translateError(code: string): string {
  switch (code) {
    case 'no_tip_distribution':
      return 'Falta elegir la distribución de propinas. Selecciónala arriba y luego inicia el cierre.';
    case 'tarifas_pendientes':
      return 'Este evento todavía no tiene tarifas. Ventas debe completarlas en el CRM antes de cerrar.';
    case 'otros_sin_flag':
      return 'Falta definir si el personal "Otro" entra en propinas. Defínelo arriba y luego inicia.';
    case 'already_closed':
      return 'Este evento ya está cerrado.';
    case 'no_team':
      return 'Este evento no tiene personal asignado, no se puede cerrar.';
    case 'event_in_future':
      return 'No se puede cerrar un evento antes de su fecha.';
    case 'event_not_found':
      return 'No encontramos el evento.';
    case 'event_protected':
      return 'El evento ya está cerrado o cancelado — no se puede cambiar la configuración.';
    case 'invalid_distribucion':
    case 'invalid_valor':
      return 'Opción inválida. Intenta de nuevo.';
    case 'no_otros_assigned':
      return 'Este evento no tiene personal "Otro" asignado.';
    case 'forbidden_not_closer':
      return 'Solo el capitán, supervisor u Operaciones puede cerrar el evento.';
    case 'forbidden_not_captain_or_admin':
      return 'Solo el capitán o el equipo de Operaciones puede reportar por otra persona.';
    case 'bridge_unavailable':
      return 'Servicio temporalmente fuera. Reintenta en un minuto.';
    default:
      return code;
  }
}
